import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ClerkProvider } from '@clerk/clerk-react'
import App from './App.tsx'
import './index.css'
import 'prismjs/themes/prism-tomorrow.css'
import './i18n/config'
import { registerServiceWorker } from './utils/registerServiceWorker'

// Sentry is initialized after the window 'load' event via dynamic import so
// the SDK (react + tracing + replay) stays out of the critical-path bundle.
// Trade-off: errors thrown before 'load' are not captured.
window.addEventListener('load', () => {
  import('./lib/monitoring')
    .then(({ initSentry }) => initSentry())
    .catch(err => console.error('Monitoring init failed:', err))
})

// Get Clerk publishable key from environment
// Force rebuild: 2025-11-05
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!clerkPubKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable')
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60, // 1 minute
    },
  },
})

// Register service worker for PWA functionality
if (import.meta.env.PROD) {
  window.addEventListener('load', () => {
    registerServiceWorker()
      .then(() => console.log('PWA features enabled'))
      .catch(err => console.error('PWA initialization failed:', err))
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={clerkPubKey}
      signInFallbackRedirectUrl="/chat"
      signUpFallbackRedirectUrl="/chat"
      afterSignOutUrl="/"
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  </React.StrictMode>,
)
